import './DrawerTargetingTable.scss';

import { useApolloClient } from '@apollo/client';
import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { User } from 'mission-control-common-components';
import React, { useMemo, useRef, useState } from 'react';

import { getPublisherTargetLineItem, writeAdTargetingEmbellishmentFieldsToCache } from '../../../common/adTargeting';
import Loader from '../../../common/Loader';
import type {
  TargetingPanelAlerts,
  TargetingPanelHeader,
  TargetingPanelTextViewer,
} from '../../../common/Targeting/TargetingPanel/TargetingPanel';
import type { AdType } from '../../../configs';
import type { PermissionsNames } from '../../../constants';
import type { Nullable } from '../../../models';
import bem from '../../../utils/bem';
import { AlertFiled } from '../TraffickingPage/cells/AlertCell';
import { getPublisherTargetingList } from '../TraffickingPage/columns';
import { EntityType } from '../TraffickingPage/hooks/makePageDrawerPlugin';
import DrawerTargetingBody from './Body';
import DrawerTargetingHeader from './Header/DrawerTargetingHeader';
import { useFetchTargeting, useUpdateTargeting } from './hooks';
import type { DrawerTargetingFormValues } from './hooks/types/formValues';
import {
  addLineItemCountryTargetingTerms,
  addPublisherToTargetingRule,
  filterOutCountryTarget,
  getLineItemCountryTargetingTerms,
} from './utils/utils';

const [block, element] = bem('drawer-targeting-table');

export type Props = Readonly<{
  id?: string;
  entityType: EntityType.AD | EntityType.LINE_ITEM;
  adType: Maybe<AdType>;
  hasInvalidPublisher?: Nullable<boolean>;
  hasInvalidCountry?: Nullable<boolean>;
}>;

function DrawerTargetingTable({
  id,
  entityType,
  adType,
  hasInvalidPublisher: initialHasInvalidPublisher,
  hasInvalidCountry: initialHasInvalidCountry,
}: Props): React.JSX.Element {
  const permissions = User.getPermissions<PermissionsNames>();
  const [editMode, setEditMode] = useState(false);
  const hasLoadedOnceRef = useRef(false);

  const { ad, lineItem, isFetching, refetch } = useFetchTargeting({
    entityType,
    id,
  });

  // Use hasInvalidPublisher from the cache (which auto-updates via useQuery),
  // falling back to the initial prop value before the query completes
  const adHasInvalidPublisher = ad.hasInvalidPublisher ?? initialHasInvalidPublisher;
  const adHasInvalidCountry = ad.hasInvalidCountry ?? initialHasInvalidCountry;
  const lineItemCountryIds = useMemo(() => lineItem.countries ?? [], [lineItem.countries]);

  const apolloClient = useApolloClient();

  const { isUpdating, submitForm } = useUpdateTargeting({
    entityType,
    id,
    lineItem: { displayPublisherTarget: lineItem.displayPublisherTarget, countries: lineItem.countries },
    fallbackAdDisplayPublisherTarget: ad.displayPublisherTarget,
    onSubmitSuccessful: async (submittedTermValues): Promise<void> => {
      setEditMode(false);
      // Best-effort refresh so anything not covered by the local cache write (e.g. server-side
      // denormalization of targetingRule) eventually converges.
      await refetch();
      // CampaignSearcher-derived fields (hasInvalidPublisher / hasInvalidCountry /
      // displayPublisherTarget / countriesTarget) lag the mutation, so the refetch above can
      // clobber the values written from the mutation `update` callback with stale data.
      // Re-apply the locally-computed values so the UI stays in sync until CampaignSearcher
      // catches up on a later refetch.
      if (entityType === EntityType.AD && id) {
        writeAdTargetingEmbellishmentFieldsToCache({
          cache: apolloClient.cache,
          adId: id,
          submittedAdTermValues: submittedTermValues,
          lineItemDisplayPublisherTarget: lineItem.displayPublisherTarget,
          lineItemCountries: lineItem.countries ?? undefined,
          fallbackAdDisplayPublisherTarget: ad.displayPublisherTarget,
        });
      }
    },
  });

  const lineItemCountryTargetingTerms = useMemo(
    () =>
      getLineItemCountryTargetingTerms({
        value: lineItem?.termValues,
      }),
    [lineItem?.termValues]
  );

  const formikInitialValues: DrawerTargetingFormValues = useMemo(
    () => ({
      adTargetingTermValues: ad.termValues,
      lineItemTargetingTermValues: filterOutCountryTarget({ value: lineItem.termValues }),
      newTargeting: {
        category: null,
        dimension: null,
        values: [],
      },
    }),
    [ad.termValues, lineItem.termValues]
  );

  const lineItemRuleWithPublisher = useMemo(() => {
    return addPublisherToTargetingRule(lineItem.targetingRule, lineItem.displayPublisherTarget);
  }, [lineItem.targetingRule, lineItem.displayPublisherTarget]);

  const lineItemPublishers = useMemo(() => getPublisherTargetLineItem(lineItem?.displayPublisherTarget) || undefined, [
    lineItem?.displayPublisherTarget,
  ]);

  const hasAccessToEdit = entityType === 'Ad' ? permissions?.updateAd : permissions?.updateLineItem;

  const headerConfig: TargetingPanelHeader = useMemo(
    () => ({
      countryLabel: entityType === EntityType.AD ? 'Line Country: ' : 'Country: ',
      publisherLabel: entityType === EntityType.AD ? 'Line Publisher: ' : 'Publisher: ',
      lineItemCountryIds,
      lineItemPublishers: getPublisherTargetingList({ value: lineItem.displayPublisherTarget }),
      hideViewToggleWhen: editMode,
      classNames: {
        viewAction: element('drawer-view-action'),
        country: element('drawer-country'),
        publisher: element('drawer-publisher'),
      },
    }),
    [entityType, lineItemCountryIds, lineItem.displayPublisherTarget, editMode]
  );

  const alertsConfig: TargetingPanelAlerts = useMemo(
    () => ({
      hasInvalidPublisher: adHasInvalidPublisher,
      hasInvalidCountry: adHasInvalidCountry,
      classNames: {
        invalidPublisher: element(AlertFiled.InvalidPublisher + '-alert'),
        invalidCountry: element(AlertFiled.InvalidCountry + '-alert'),
      },
    }),
    [adHasInvalidPublisher, adHasInvalidCountry]
  );

  const textViewerConfig: TargetingPanelTextViewer = useMemo(
    () => ({
      entityType,
      adRule: ad.targetingRule,
      lineItemRule: lineItemRuleWithPublisher,
      // The drawer drops the user back to the table view whenever editMode flips
      // on, even mid-text-view. See TargetingPanel.test.tsx for the contract.
      forceTableView: editMode,
    }),
    [entityType, ad.targetingRule, lineItemRuleWithPublisher, editMode]
  );

  // Once the drawer has loaded its targeting data at least once, a subsequent fetch is a
  // post-save reconcile refetch — keep the current content visible-but-dimmed under an
  // "Updating…" overlay instead of blanking the whole panel back to a spinner.
  if (!hasLoadedOnceRef.current && !isFetching) {
    hasLoadedOnceRef.current = true;
  }
  const showReloadOverlay = isFetching && hasLoadedOnceRef.current;

  if (isFetching && !hasLoadedOnceRef.current) return <Loader text="Loading..." />;

  function submitFormWithCountry(
    values: DrawerTargetingFormValues,
    actions: FormikHelpers<DrawerTargetingFormValues>
  ): void {
    const valuesWithCountry =
      entityType === 'Ad' ? values : addLineItemCountryTargetingTerms(values, lineItemCountryTargetingTerms);
    const targetingRuleToSubmit = entityType === 'Ad' ? ad.targetingRule : lineItem.targetingRule;
    submitForm(valuesWithCountry, actions, targetingRuleToSubmit);
  }

  return (
    <div className={block()}>
      {showReloadOverlay && (
        <output className={element('reload-status')} data-testid="drawer-targeting-reload-status" aria-live="polite">
          <span className={element('reload-status-pill')}>
            <Loader />
            Updating…
          </span>
        </output>
      )}
      <div className={element('content', showReloadOverlay ? 'reloading' : undefined)}>
        <Formik initialValues={formikInitialValues} onSubmit={submitFormWithCountry} enableReinitialize>
          {({ submitForm, resetForm, isSubmitting }): JSX.Element => (
            <div>
              <DrawerTargetingHeader
                editMode={editMode}
                disableSubmitting={isSubmitting || isUpdating}
                canEdit={hasAccessToEdit}
                setEditMode={setEditMode}
                onSubmit={submitForm}
                onCancel={resetForm}
                loading={isUpdating}
              />
              <DrawerTargetingBody
                editMode={editMode}
                entityType={entityType}
                lineItemCountryIds={lineItemCountryIds}
                lineItemPublishers={lineItemPublishers}
                adType={adType}
                allowEditCountryTarget={
                  entityType === 'Ad' && (ad.hasInvalidCountry || (lineItemCountryIds?.length ?? 0) > 1)
                }
                header={headerConfig}
                alerts={alertsConfig}
                textViewer={textViewerConfig}
              />
            </div>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default DrawerTargetingTable;
